// src/components/BoletoModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../services/api';

const instancia = process.env.REACT_APP_INSTANCIA;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.3rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Radio = styled.input`
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const CancelButton = styled.button`
  padding: 10px 16px;
  border: 1px solid #ddd;
  background-color: white;
  color: #333;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const GenerateButton = styled.button`
  padding: 10px 16px;
  border: none;
  background-color: #4a90e2;
  color: white;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    background-color: #3a80d2;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  margin-top: 16px;
  text-align: center;
`;

const LoadingMessage = styled.div`
  color: #666;
  margin-top: 16px;
  text-align: center;
`;

const BoletoModal = ({ isOpen, onClose, pedidoId, selecionados }) => {
  const [bancos, setBancos] = useState([]);
  const [selectedBanco, setSelectedBanco] = useState('');
  const [mesGeracao, setMesGeracao] = useState('atual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingBancos, setLoadingBancos] = useState(true);

  // Carregar lista de bancos quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      const fetchBancos = async () => {
        try {
          setLoadingBancos(true);
          const response = await api.get('/layoutBoleto', {
            params: {
              "instancia": `${instancia}`
            }
          });
          setBancos(response.data);

          // Selecionar o primeiro banco por padrão se houver dados
          if (response.data && response.data.length > 0) {
            setSelectedBanco(response.data[0].id);
          }

          setError('');
        } catch (err) {
          console.error('Erro ao carregar bancos:', err);
          setError('Não foi possível carregar a lista de bancos. Por favor, tente novamente.');
        } finally {
          setLoadingBancos(false);
        }
      };
      fetchBancos();
    }
  }, [isOpen]);

  const handleGerarBoleto = async () => {
    try {
      setLoading(true);
      setError('');

      // Chamar a API para gerar o boleto
      await api.post('/gerarBoletos', {
        id: pedidoId,
        tipo: 'NFe',
        bancoId: selectedBanco,
        mes: mesGeracao
      });

      // Fechar o modal após sucesso
      onClose();

      // Opcional: Você pode adicionar uma notificação de sucesso aqui
      alert('Boleto gerado com sucesso!');
    } catch (err) {
      console.error('Erro ao gerar boleto:', err);
      setError('Não foi possível gerar o boleto. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Gerar Boleto</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <FormGroup>
          <Label htmlFor="banco">Banco</Label>
          {loadingBancos ? (
            <LoadingMessage>Carregando bancos...</LoadingMessage>
          ) : (
            <Select
              id="banco"
              value={selectedBanco}
              onChange={(e) => setSelectedBanco(e.target.value)}
              disabled={loading}
            >
              {bancos.length === 0 ? (
                <option value="">Nenhum banco disponível</option>
              ) : (
                bancos.map((banco) => (
                  <option key={banco.id} value={banco.id}>
                    {banco.descricao}
                  </option>
                ))
              )}
            </Select>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Mês de Geração</Label>
          <RadioGroup>
            <RadioLabel>
              <Radio
                type="radio"
                name="mesGeracao"
                value="atual"
                checked={mesGeracao === 'atual'}
                onChange={() => setMesGeracao('atual')}
                disabled={loading}
              />
              Mês Atual
            </RadioLabel>
            <RadioLabel>
              <Radio
                type="radio"
                name="mesGeracao"
                value="proximo"
                checked={mesGeracao === 'proximo'}
                onChange={() => setMesGeracao('proximo')}
                disabled={loading}
              />
              Próximo Mês
            </RadioLabel>
          </RadioGroup>
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <CancelButton onClick={onClose} disabled={loading}>
            Cancelar
          </CancelButton>
          <GenerateButton
            onClick={handleGerarBoleto}
            disabled={loading || !selectedBanco || bancos.length === 0}
          >
            {loading ? 'Gerando...' : 'Gerar Boleto'}
          </GenerateButton>
        </ButtonGroup>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default BoletoModal;